import React, { Component, useCallback, useEffect, useState } from 'react'
import connect from 'front-end-core/decorators/connect'
import cx from 'classnames'
import { isEqual, result, filter, uniqBy } from 'libraries/lodash'
import MuiButton from '@material-ui/core/Button'
import Link from '@material-ui/core/Link'
import Grid from '@material-ui/core/Grid'
import withStyles from '@material-ui/core/styles/withStyles'
import { pushUrl, replaceUrl } from 'front-end-core/core/history'
import url from 'front-end-core/core/url'
import { MW_LIST_DEALS, SEARCH } from 'constants/controllers'
import { getDocumentConfiguration } from 'services/DealwatchService'
import { parametersToString, parametersFromString, extractParameters } from './params'
import ResponseConsumer from 'components/shared/ResponseConsumer'
import Icon from 'components/shared/Icon'
import FilterOverlay, { FilterWrapper, Filters } from './Overlay'
import { useSelector } from 'react-redux'
import selectAolName from 'redux/selectors/selectAolName'

const ConnectedFiltersPanel = connect(() => ({
  documentConfiguration: getDocumentConfiguration()
}))(props => (
  <ResponseConsumer
    response={props.documentConfiguration}
    content={() => <FiltersPanel {...props} />}
  />
))

const FiltersPanel = (props) => {
  const aol = useSelector(selectAolName)
  const { dealType, searchPhrase = '' } = props
  const [parameters, setParameters] = useState(extractParameters(props))
  const [filterValues, setFilterValues] = useState()
  const [filters, setFilters] = useState([])
  const [documentParameters, setDocumentParameters] = useState()

  useEffect(() => {
    const parameters = extractParameters(props)
    setParameters(parameters)
  }, [props])

  useEffect(() => {
    try {
      setFilterValues(new Filters(
        ...parametersFromString(parameters)
      ))
    } catch (e) {
      setFilterValues(new Filters())
    }
  }, [parameters])

  useEffect(() => {
    const _parameters = result(
      props.documentConfiguration,
      [props.dealType, 'parameters'],
      {}
    )
    setDocumentParameters(_parameters)
  }, [props.documentConfiguration, props.dealType])

  useEffect(() => {
    const _filters = filter(documentParameters || [], p => p.filterGroup ? p.filterGroup.type === 'Basic' : true)
    setFilters(_filters)
  }, [documentParameters])

  const updateFilters = useCallback((payload, push = 1) => {
    const _parameters = parametersToString(payload)
    const route = url(MW_LIST_DEALS, { aol, dealType }, { parameters: _parameters })

    if (!push) {
      setParameters(_parameters)
      replaceUrl(route)
    } else {
      pushUrl(route)
    }
  }, [aol, dealType])

  const resetFilterValue = useCallback((name) => () => {
    const _filters = (new Filters(...filterValues)).removeAll(name)
    updateFilters([..._filters], 0)
  }, [filterValues])

  return (
    <div className='mh6'>
      {searchPhrase && (
        <div className='bg-ghost-grey'>
          <h2 className='b f4 black lh-title mb7'>
            Search type
          </h2>
          <ButtonSearchPageLinks {...{ searchPhrase, aol }} selectedPage='markets' />
        </div>
      )}
      <div className='f6 pt1 ph5 pb9 bg-white mb6'>
        <SeeAllFilters {...{ ...props, filterValues, aol }} />
        {filters.map((parameter, key) => (
          <Filter key={key} {...{ parameter, filterValues, resetFilterValue, updateFilters }} />
        ))}
      </div>
    </div>
  )
}

const Filter = ({ parameter, filterValues, resetFilterValue, updateFilters }) => {
  const { name, description } = parameter
  const filterValue = filterValues.get(name)

  const setFilterValue = (name) => (values) => {
    const _filters = (new Filters(...filterValues)).setValues(name, values)
    updateFilters([..._filters], 0)
  }

  return (
    <div className='mt6'>
      <div className='relative'>
        <div className='bb mb2'>{description}</div>
        {filterValue.length > 0 && (
          <a
            href={null}
            className='absolute top0 right0 pointer cadet-blue hover-underline f7'
            onClick={resetFilterValue(name)}
          >
            <i>reset</i>
          </a>
        )}
      </div>
      <div className='pa0 db'>
        <FilterWrapper
          {...parameter}
          setFilterValue={setFilterValue(name)}
          filterValue={filterValue}
          basicDisplay
        />
      </div>
    </div>
  )
}

const Button = withStyles({
  root: {
    height: '3rem',
    border: 'solid 1px #6288a5 !important',
    boxShadow: 'none',
    '&:disabled': {
      backgroundColor: '#008C9F'
    },
    '&:hover': {
      backgroundColor: 'rgba(0,140,159,0.15)'
    }
  }
})(MuiButton)

const ButtonSearchPageLinks = (props) => (
  <div>
    <SearchTypeButton {...props} searchType='qa' title='Q&A' iconName='icon-qa-search-variant' iconCxName='svg-fill-cadet-blue' flavour='full' />
    <SearchTypeButton {...props} searchType='legislation' title='Legislation' iconName='icon-legislation-search-variant' iconCxName='svg-fill-cadet-blue' />
    <SearchTypeButton {...props} searchType='ca' title='News' iconName='icon-news-archive-search-variant' iconCxName='svg-fill-cadet-blue' />
    <SearchTypeButton {...props} searchType='precedents' title='Precedents' iconName='icon-precedents-search-variant' iconCxName='svg-fill-cadet-blue' />
    <SearchTypeButton {...props} searchType='markets' title='Markets' iconName='icon-markets-search-variant' iconCxName='svg-stroke-white' />
  </div>
)

const SearchTypeButton = ({ searchPhrase, selectedPage, aol, searchType, title, iconName, iconCxName, flavour }) => {
  const selected = selectedPage === searchType

  return (
    <Link
      href={selected ? null : getSearchUrl(aol, searchType, searchPhrase, flavour)}
      className={cx({ selected: 'no-underline' })}
      onClick={e => selected ? e.preventDefault() : null}
    >
      <Button
        className='w-100 mb3'
        variant='contained'
        classes={{ root: 'bg-white ttc b f5' }}
        disabled={selected}
      >
        <Grid
          container
          justify='space-between'
          alignItems='center'
          className={cx(
            'w-100 pv0', {
              white: selected,
              'cadet-blue': !selected
            })}
        >
          <Grid item>
            {title}
          </Grid>
          <Grid item>
            <Icon className={cx('h-100 w7 vm', iconCxName)} name={iconName} />
          </Grid>
        </Grid>
      </Button>
    </Link>
  )
}

const getSearchUrl = (aol, searchType, searchPhrase, flavour) => {
  if (searchType === 'markets') {
    return url(MW_LIST_DEALS, { aol, dealType: 'all' }, { q: searchPhrase })
  } else {
    return url(SEARCH, { aol, searchType, flavour }, { q: searchPhrase })
  }
}

export class SeeAllFilters extends Component {
  static defaultProps = {
    inline: null
  }

  getFilterValues (props = this.props) {
    const { parameters = [] } = props
    return parametersFromString(parameters)
  }

  onSubmit = (payload) => {
    const { aol, dealType } = this.props
    const parameters = parametersToString(payload)
    const route = url(MW_LIST_DEALS, { aol, dealType }, { parameters })
    pushUrl(route)
  }

  handleToggleFilters = () => {
    const filterValues = this.getFilterValues()
    const { actions, dealType, documentConfiguration, filters, pageToggleOverlay, browser } = this.props
    const { eventDate, eventType, parameters } = documentConfiguration[dealType]
    const onSubmit = this.onSubmit

    pageToggleOverlay(
      <FilterOverlay
        {...{
          actions,
          dealType,
          eventDate,
          eventType,
          filterValues,
          filters,
          onSubmit,
          parameters,
          pageToggleOverlay,
          browser
        }}
      />
    )
  }

  UNSAFE_componentWillReceiveProps(nextProps, _) { // eslint-disable-line
    !isEqual(this.getFilterValues(this.props), this.getFilterValues(nextProps)) &&
      this.props.pageToggleOverlay(null)
  }

  render () {
    const { dealType, documentConfiguration, isLargeDesktop, searchPhrase, windowWidth, inline } = this.props
    if (dealType === 'all' || !Object.prototype.hasOwnProperty.call(documentConfiguration, dealType)) {
      return null
    }
    const size = uniqBy(this.getFilterValues(), 'name').length
    const suffix = size
      ? `${size} filter${size > 1 ? 's' : ''} active`
      : ''

    return inline
      ? (
        <a href={null} onClick={this.handleToggleFilters} className='pointer'>
          <span>{inline}</span>
        </a>
        )
      : !inline
          ? (
            <button
              onClick={this.handleToggleFilters}
              className={cx('pointer bg-tangerine-dream lh-copy--spaced br2 ph6 transition-all charcoal ba b--light-grey tl', {
              // Prevents this element from overlapping with close left button
                'w-100': isLargeDesktop || searchPhrase,
                'w-90': !isLargeDesktop,
                // Prevents markets nav toggle from overlapping with this element
                mt8: !searchPhrase && windowWidth < 960,
                mt4: !searchPhrase && windowWidth > 960
              })}
            >
              See more filters...
              {suffix}
            </button>
            )
          : null
  }
}

export default ConnectedFiltersPanel
